import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import Navigation from "@/components/maison/Navigation";
import Footer from "@/components/maison/Footer";
import UFJPrivateClientDialog from "@/components/maison/ultrafine/UFJPrivateClientDialog";
import PDPGallery from "@/components/maison/ultrafine/pdp/PDPGallery";
import PDPSpecs from "@/components/maison/ultrafine/pdp/PDPSpecs";
import PDPActions from "@/components/maison/ultrafine/pdp/PDPActions";
import { fetchPiece } from "@/lib/api";

export default function JewelryPiecePage() {
  const { slug } = useParams();
  const [piece, setPiece] = useState(null);
  const [error, setError] = useState(false);
  const [dialog, setDialog] = useState({ open: false, mode: "inquire" });
  const dialogPiece = useMemo(
    () => (piece ? { name: `${piece.official_name || piece.name} (${piece.reference})` } : null),
    [piece]
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    setPiece(null);
    setError(false);
    fetchPiece(slug).then(setPiece).catch(() => setError(true));
  }, [slug]);

  useEffect(() => {
    if (piece) document.title = `${piece.official_name || piece.name} — PiérrecDavís Ultra Fine Jewelry`;
  }, [piece]);

  return (
    <div className="bg-black text-white min-h-screen" data-testid="jewelry-piece-page">
      <Navigation />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-28">
        <Link to="/ultra-fine-jewelry" className="inline-flex items-center gap-3 eyebrow text-white/60 hover:text-[var(--pd-gold)] transition-colors" data-testid="pdp-back-link">
          <ArrowLeft size={14} /> Ultra Fine Jewelry
        </Link>
        {error && <NotFound />}
        {!error && !piece && <p className="mt-20 eyebrow text-white/40" data-testid="pdp-loading">Preparing the piece…</p>}
        {piece && <PieceLayout piece={piece} onInquire={(mode) => setDialog({ open: true, mode })} />}
      </main>
      <Footer />
      <UFJPrivateClientDialog
        open={dialog.open}
        mode={dialog.mode}
        piece={dialogPiece}
        onClose={() => setDialog((d) => ({ ...d, open: false }))}
      />
    </div>
  );
}

function PieceLayout({ piece, onInquire }) {
  return (
    <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
      <div className="lg:col-span-7"><PDPGallery piece={piece} /></div>
      <div className="lg:col-span-5 lg:sticky lg:top-28 self-start space-y-10">
        <div>
          <p className="eyebrow" data-testid="pdp-category">{piece.category_name}</p>
          <h1 className="mt-5 font-serif-display text-4xl sm:text-5xl text-white tracking-[0.05em] leading-[1.08]" data-testid="pdp-name">
            {piece.official_name || piece.name}
          </h1>
          <p className="mt-3 text-white/45 text-[0.62rem] uppercase tracking-[0.4em]" data-testid="pdp-reference">
            Reference {piece.reference}
          </p>
          {!piece.official_name && (
            <p className="mt-2 font-serif-body italic text-white/40 text-sm" data-testid="pdp-official-name-note">
              Official name revealed at private viewing
            </p>
          )}
          <div className="hairline my-7" />
          <p className="font-serif-body text-lg text-white/75 leading-relaxed" data-testid="pdp-story">{piece.story}</p>
        </div>
        <PDPActions piece={piece} onInquire={onInquire} />
        <PDPSpecs specs={piece.specs} />
      </div>
    </div>
  );
}

function NotFound() {
  return (
    <div className="mt-24 max-w-xl" data-testid="pdp-not-found">
      <h1 className="font-serif-display text-4xl text-white tracking-[0.06em]">This piece is held privately.</h1>
      <p className="mt-4 text-white/55">It may have been placed with a client or is not yet released.</p>
      <Link to="/ultra-fine-jewelry" className="btn-gold mt-8 inline-flex"><span>Return to the Collection</span></Link>
    </div>
  );
}
